/**
 * Singleton store for transient notices (toasts).
 *
 * Lives at module scope so notices survive React-tree unmounts. ReactiveJsonRoot
 * is remounted on every route change, so any state held inside a route
 * component is lost across navigation. A module-scope singleton sidesteps that.
 *
 * Lifecycle: persists across in-app navigation, lost on full page reload (F5).
 */

const DEFAULT_DURATION = 10000;
const VALID_TYPES = new Set(["info", "success", "warning", "error"]);

class NoticeStore {
    constructor() {
        this.notices = [];
        this.listeners = new Set();
        this.nextId = 1;
    }

    /**
     * Push a notice onto the stack.
     *
     * @param {{type?: string, message: string, duration?: number}} input
     * @returns {number} The id of the created notice.
     */
    add(input) {
        const type = VALID_TYPES.has(input?.type) ? input.type : "info";
        const message = typeof input?.message === "string" ? input.message : String(input?.message ?? "");
        const duration = Number.isFinite(input?.duration) && input.duration > 0
            ? input.duration
            : DEFAULT_DURATION;

        const notice = {
            id: this.nextId++,
            type,
            message,
            duration,
            createdAt: Date.now(),
        };

        this.notices = [...this.notices, notice];
        this.emit();
        return notice.id;
    }

    remove(id) {
        const next = this.notices.filter((n) => n.id !== id);
        if (next.length !== this.notices.length) {
            this.notices = next;
            this.emit();
        }
    }

    clear() {
        if (this.notices.length === 0) return;
        this.notices = [];
        this.emit();
    }

    subscribe(listener) {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    getAll() {
        return this.notices;
    }

    emit() {
        for (const listener of this.listeners) {
            listener();
        }
    }
}

export const noticeStore = new NoticeStore();

/**
 * Convenience helper. Imperative usage from any JSX plugin or service:
 *   notify({ type: 'error', message: 'oups' })
 *
 * @param {{type?: string, message: string, duration?: number}} input
 * @returns {number} The id of the created notice.
 */
export const notify = (input) => noticeStore.add(input);

export { DEFAULT_DURATION };
